import React from 'react';
import {
  FileText, BarChart3, ListTodo, Clock, Newspaper,
  CheckCircle2, Circle,
} from 'lucide-react';
import { STATUS_COLOR, CATEGORY_COLOR, PRIORITY_COLOR } from '../../data/agents';

function SummaryCards({ data }) {
  if (!data) return null;
  const { summary, health, sports, todos } = data;
  const cards = [
    { label: '오늘 발행', value: summary.todayPosts, sub: `이번 주 ${summary.weekPosts}건`, color: 'var(--green)', Icon: FileText },
    { label: '전체 포스트', value: summary.totalPosts, sub: `건강 ${health.total} · 스포츠 ${sports.total}`, color: 'var(--cyan)', Icon: BarChart3 },
    { label: '미완료 할일', value: todos.pending, sub: `전체 ${todos.total}건 중`, color: 'var(--red)', Icon: ListTodo },
    { label: '오늘 할일', value: summary.todayTodos, sub: '오늘 마감', color: 'var(--orange)', Icon: Clock },
  ];
  return (
    <div className="n-summary-grid">
      {cards.map((c, i) => (
        <div key={i} className="n-summary-card" style={{ '--accent': c.color }}>
          <div className="n-summary-icon"><c.Icon size={18} strokeWidth={1.8} /></div>
          <div className="n-summary-body">
            <div className="n-summary-label">{c.label}</div>
            <div className="n-summary-value">{c.value}</div>
            <div className="n-summary-sub">{c.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RecentPosts({ data }) {
  if (!data) return null;
  const { health, sports } = data;
  const allPosts = [
    ...health.recent.map(p => ({ ...p, type: 'health', title: p.제목, date: p.날짜 })),
    ...sports.recent.map(p => ({ ...p, type: 'sports', title: p.이름, date: p.created?.split('T')[0] })),
  ].sort((a, b) => (b.date || '').localeCompare(a.date || '')).slice(0, 8);

  if (allPosts.length === 0) return null;

  return (
    <div className="section">
      <div className="n-section-title"><Newspaper size={14} /><span>최근 포스트</span></div>
      <div className="n-card" style={{ padding: '4px 0' }}>
        {allPosts.map((item, i) => (
          <div key={i} className="n-post-row">
            <span className="n-post-row-type" style={{
              color: item.type === 'health' ? 'var(--type-health)' : 'var(--type-sports)',
            }}>
              {item.type === 'health' ? '건강' : '스포'}
            </span>
            {item.상태 && (
              <span className="n-post-row-status" style={{ color: STATUS_COLOR[item.상태] || 'var(--txt3)' }}>
                {item.상태}
              </span>
            )}
            <span className="n-post-row-title">{item.title}</span>
            <span className="n-post-row-date">{(item.date || '').slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuickTodos({ data, onToggle, onOfficeTab }) {
  if (!data) return null;
  const urgent = data.todos.recent
    .filter(t => t.상태 !== '완료')
    .sort((a, b) => {
      const da = a.날짜 || '9999-99-99';
      const db = b.날짜 || '9999-99-99';
      return da.localeCompare(db); // 가까운 날짜 먼저
    })
    .slice(0, 6);
  if (urgent.length === 0) return null;

  return (
    <div className="section" style={{ marginBottom: 20 }}>
      <div className="n-section-title">
        <ListTodo size={14} /><span>오늘 할일</span>
        {onOfficeTab && <button className="n-section-link" onClick={onOfficeTab} style={{ marginLeft: 'auto' }}>전체 →</button>}
      </div>
      <div className="n-card" style={{ padding: '4px 0' }}>
        {urgent.map(item => (
          <QuickTodoRow key={item.id} item={item} onToggle={onToggle} />
        ))}
      </div>
    </div>
  );
}

function QuickTodoRow({ item, onToggle }) {
  const [updating, setUpdating] = React.useState(false);
  const [optimistic, setOptimistic] = React.useState(null); // null = item.상태 기준
  const isDone = optimistic !== null ? optimistic : item.상태 === '완료';
  const handleClick = async () => {
    if (updating) return;
    const newDone = !isDone;
    setOptimistic(newDone); // 즉시 반영
    setUpdating(true);
    try { await onToggle(item.id, newDone ? '완료' : '미완료'); }
    catch { setOptimistic(null); } // 오류 시 원복
    finally { setUpdating(false); }
  };
  const catColor = CATEGORY_COLOR[item.카테고리] || 'var(--txt3)';
  const prColor  = PRIORITY_COLOR[item.우선순위]  || null;
  return (
    <div className={`n-quick-todo-row ${isDone ? 'done' : ''}`} onClick={handleClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleClick()} aria-label={`${item.이름} 완료 토글`}>
      <span className="n-quick-todo-check">
        {isDone
          ? <CheckCircle2 size={14} style={{ color: 'var(--green)', opacity: updating ? 0.6 : 1 }} />
          : <Circle size={14} style={{ color: updating ? 'var(--accent)' : 'var(--brd-hover)', opacity: updating ? 0.6 : 1 }} />}
      </span>
      <span className="n-quick-todo-name">{item.이름}</span>
      {item.메모 && <span className="n-quick-todo-memo">{item.메모}</span>}
      <div className="n-quick-todo-tags">
        {item.카테고리 && <span style={{ color: catColor, fontSize: 10 }}>{item.카테고리}</span>}
        {item.우선순위 === '🔴 높음' && <span className="n-priority-dot high" style={{ fontSize: 10 }}>높음</span>}
        {item.우선순위 === '🟡 보통' && <span className="n-priority-dot mid" style={{ fontSize: 10 }}>보통</span>}
        {item.날짜 && <span style={{ color: 'var(--txt3)', fontSize: 10, fontFamily: 'Outfit' }}>{item.날짜.slice(5)}</span>}
      </div>
    </div>
  );
}

function TodoItem({ item, onToggle }) {
  const [updating, setUpdating] = React.useState(false);
  const [optimistic, setOptimistic] = React.useState(null);
  const isDone = optimistic !== null ? optimistic : item.상태 === '완료';
  const handleClick = async () => {
    if (updating) return;
    const newDone = !isDone;
    setOptimistic(newDone);
    setUpdating(true);
    try { await onToggle(item.id, newDone ? '완료' : '미완료'); }
    catch { setOptimistic(null); }
    finally { setUpdating(false); }
  };
  return (
    <div className={`n-card n-todo-item ${isDone ? 'done' : ''} ${updating ? 'syncing' : ''}`} onClick={handleClick} style={{ cursor: 'pointer' }}
      role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && handleClick()} aria-label={`${item.이름} 완료 토글`}>
      <div className="n-todo-check-wrap">
        {isDone
          ? <CheckCircle2 size={18} style={{ color: 'var(--green)', opacity: updating ? 0.6 : 1 }} />
          : <Circle size={18} style={{ color: updating ? 'var(--accent)' : 'var(--brd)', opacity: updating ? 0.6 : 1 }} />}
      </div>
      <div className="n-todo-content">
        <div className={`n-todo-name ${isDone ? 'strike' : ''}`}>{item.이름}</div>
        {item.메모 && <div className="n-todo-memo">{item.메모}</div>}
        <div className="n-todo-tags">
          {item.카테고리 && <span className="n-tag" style={{ color: CATEGORY_COLOR[item.카테고리] || 'var(--txt3)' }}>{item.카테고리}</span>}
          {item.우선순위 === '🔴 높음' && <span className="n-priority-dot high">높음</span>}
          {item.우선순위 === '🟡 보통' && <span className="n-priority-dot mid">보통</span>}
          {item.날짜 && <span className="n-tag dim">{item.날짜}</span>}
        </div>
      </div>
    </div>
  );
}

export { SummaryCards, RecentPosts, QuickTodos };
